import { useState, useEffect } from 'react'
import { useCollaboration } from '../stores/CollaborationContext'
import { useAuth } from '../stores/AuthContext'
import './HelpModal.css' // Reuse modal styles
import './ShareModal.css'

export default function ShareModal({ isOpen, onClose, projectId, projectName, isOwner }) {
  const { user } = useAuth()
  const { shares, fetchShares, shareProject, removeShare, updateShareRole } = useCollaboration()
  const [email, setEmail] = useState('')
  const [role, setRole] = useState('VIEWER')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(null)

  useEffect(() => {
    if (isOpen && projectId) {
      fetchShares(projectId)
    }
  }, [isOpen, projectId, fetchShares])

  const handleShare = async (e) => {
    e.preventDefault()
    setError(null)
    setSuccess(null)
    setLoading(true)

    const result = await shareProject(projectId, email, role)

    if (result.success) {
      setEmail('')
      setRole('VIEWER')
      setSuccess(`Successfully shared with ${email}`)
      setTimeout(() => setSuccess(null), 3000)
    } else {
      setError(result.error)
    }

    setLoading(false)
  }

  const handleRemove = async (userId, userEmail) => {
    if (!confirm(`Remove ${userEmail}'s access?`)) return

    setLoading(true)
    setError(null)
    const result = await removeShare(projectId, userId)

    if (!result.success) {
      setError(result.error)
    }

    setLoading(false)
  }

  const handleRoleChange = async (userId, newRole) => {
    setLoading(true)
    setError(null)

    const result = await updateShareRole(projectId, userId, newRole)

    if (!result.success) {
      setError(result.error)
    }

    setLoading(false)
  }

  if (!isOpen) return null

  const canManageSharing = isOwner // Only owner can share for now

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-container share-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>👥 Share "{projectName}"</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        <div className="modal-body">
          {canManageSharing && (
            <form onSubmit={handleShare} className="share-form">
              <h3>Invite User</h3>
              <p className="share-help-text">
                Enter the email address of someone who already has a TeDdO account.
              </p>

              <div className="form-group">
                <label htmlFor="share-email">Email address</label>
                <input
                  id="share-email"
                  type="email"
                  placeholder="user@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  disabled={loading}
                />
              </div>

              <div className="form-group">
                <label htmlFor="share-role">Permission level</label>
                <select
                  id="share-role"
                  value={role}
                  onChange={(e) => setRole(e.target.value)}
                  disabled={loading}
                >
                  <option value="VIEWER">Viewer - Can view only</option>
                  <option value="EDITOR">Editor - Can edit project</option>
                  <option value="ADMIN">Admin - Can edit and share</option>
                </select>
              </div>

              {error && <div className="error-message">{error}</div>}
              {success && <div className="success-message">{success}</div>}

              <button type="submit" className="button-primary" disabled={loading}>
                {loading ? 'Sharing...' : 'Share Project'}
              </button>
            </form>
          )}

          <div className="shares-list">
            <h3>People with Access</h3>

            {/* Owner */}
            <div className="share-item owner">
              <div className="share-user">
                <div
                  className="user-avatar"
                  style={{ backgroundColor: stringToColor(user?.email || '') }}
                >
                  {getInitials(user)}
                </div>
                <div className="user-info">
                  <div className="user-name">{user?.name || user?.email}</div>
                  <div className="user-role-badge owner-badge">Owner</div>
                </div>
              </div>
            </div>

            {/* Shared users */}
            {shares.map((share) => (
              <div key={share._id} className="share-item">
                <div className="share-user">
                  <div
                    className="user-avatar"
                    style={{ backgroundColor: stringToColor(share.userEmail || share.userId) }}
                  >
                    {getInitials({ name: share.userName, email: share.userEmail })}
                  </div>
                  <div className="user-info">
                    <div className="user-name">
                      {share.userName || share.userEmail || share.userId}
                    </div>
                    {share.userEmail && <div className="user-email">{share.userEmail}</div>}
                  </div>
                </div>

                {canManageSharing ? (
                  <div className="share-actions">
                    <select
                      value={share.role}
                      onChange={(e) => handleRoleChange(share.userId, e.target.value)}
                      disabled={loading}
                      className="role-select"
                    >
                      <option value="VIEWER">Viewer</option>
                      <option value="EDITOR">Editor</option>
                      <option value="ADMIN">Admin</option>
                    </select>
                    <button
                      className="button-remove"
                      onClick={() => handleRemove(share.userId, share.userEmail || share.userId)}
                      disabled={loading}
                      title="Remove access"
                    >
                      ×
                    </button>
                  </div>
                ) : (
                  <div className="share-role-badge">{share.role}</div>
                )}
              </div>
            ))}

            {shares.length === 0 && (
              <div className="empty-state">
                <p>Not shared with anyone yet.</p>
                {canManageSharing && <p>Use the form above to invite collaborators.</p>}
              </div>
            )}
          </div>
        </div>

        <div className="modal-footer">
          <button onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  )
}

// Helper functions
function getInitials(user) {
  if (!user) return '?'
  if (user.name) {
    return user.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
  }
  return user.email?.[0]?.toUpperCase() || '?'
}

function stringToColor(str) {
  let hash = 0
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash)
  }

  const hue = hash % 360
  return `hsl(${hue}, 65%, 50%)`
}
